import React, { useState, useEffect, useCallback, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import PidBagModal from '../components/PidBagModal';
import PidTableShell, { CommodityChips } from '../components/PidTableShell';
import { PageWrapper, ContentWrapper } from '../styles/PackageIndexStyles';
import StatusPicker from '../components/StatusPicker';
import DependentsBlockDialog from '../components/DependentsBlockDialog';
import { PID_STATUSES, pidStatusColor, normalizePidStatus, pidStatusRank } from '../utils/pidStatus';
import { changePidStatus } from '../utils/pidStatusApi';

const PidBagsPage = () => {
  const [bags, setBags] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [packTypeMap, setPackTypeMap] = useState(new Map());
  const [packTypeOptions, setPackTypeOptions] = useState([]); // [{value,label}]
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editGroup, setEditGroup] = useState(null); // { labelId, baseInfo, commodities: {name: string[], ...}, rows: [] }
  const [loading, setLoading] = useState(true);
  const [dep, setDep] = useState(null); // { name, dependents } when a label inactivation is blocked

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [bagsRes, commoditiesRes, packTypesRes] = await Promise.all([
        authAxios.get('/api/pidbags'),
        authAxios.get('/api/commodities'),
        authAxios.get('/api/packtypes'),
      ]);
      setBags(Array.isArray(bagsRes.data) ? bagsRes.data : []);
      setCommodities(Array.isArray(commoditiesRes.data) ? commoditiesRes.data : []);
      const rows = Array.isArray(packTypesRes.data) ? packTypesRes.data : [];
      const map = new Map();
      const opts = [];
      const seen = new Set();
      for (const r of rows) {
        const id = Number(r.UniqueID);
        const style = String(r.Style || '').trim();
        if (!style) continue;
        if (!map.has(id)) map.set(id, style);
        if (!seen.has(style)) { seen.add(style); opts.push({ value: id, label: style }); }
      }
      setPackTypeMap(map);
      setPackTypeOptions(opts);
    } catch (err) {
      console.error('[PID Bags] Fetch error:', err);
      setBags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleStatus = useCallback(async (labelId, next, name) => {
    const lid = String(labelId);
    const prevById = {};
    setBags(rs => {
      rs.forEach(r => { if (String(r.LABEL_ID) === lid) prevById[r.UniqueID] = r.Status; });
      return rs.map(r => (String(r.LABEL_ID) === lid ? { ...r, Status: next } : r));
    });
    const revert = () => setBags(rs => rs.map(r => (String(r.LABEL_ID) === lid ? { ...r, Status: prevById[r.UniqueID] ?? r.Status } : r)));
    try {
      const result = await changePidStatus('bags', lid, next);
      if (result.blocked) {
        revert();
        setDep({ name: name || lid, dependents: result.dependents });
      }
    } catch (e) {
      console.error('[PidBags] status change failed', e);
      revert();
      alert(e?.response?.data?.error || 'Failed to update status.');
    }
  }, []);

  // Group rows by Label ID with nested Commodity -> Styles; one table row per label group.
  const groups = useMemo(() => {
    const m = new Map();
    for (const row of bags) {
      const labelId = String(row.LABEL_ID || '').trim();
      if (!m.has(labelId)) m.set(labelId, { rows: [], commodities: new Map(), baseInfo: null });
      const g = m.get(labelId);
      g.rows.push(row);
      if (!g.baseInfo) {
        g.baseInfo = {
          ItemNumber: row.ItemNumber,
          Retailer: row.Retailer,
          Item_Description: row.Item_Description,
          LABEL_ID: row.LABEL_ID,
          Type: row.Type,
          UPC: row.UPC,
        };
      }
      const commodity = String(row.Commodity || '').trim();
      if (!g.commodities.has(commodity)) g.commodities.set(commodity, new Set());
      const styleIds = String(row.Styles || '')
        .split(',')
        .map(s => Number(String(s).trim()))
        .filter(n => Number.isFinite(n));
      for (const id of styleIds) {
        const styleName = packTypeMap.get(id) || String(id);
        g.commodities.get(commodity).add(styleName);
      }
    }
    const out = [];
    for (const [labelId, g] of m.entries()) {
      const comms = Array.from(g.commodities.entries()).map(([name, set]) => ({
        name,
        styles: Array.from(set).sort((a, b) => String(a).localeCompare(String(b))),
      }));
      out.push({
        labelId,
        baseInfo: g.baseInfo,
        rows: g.rows,
        commodities: comms,
        commodityNames: comms.map(c => c.name).filter(Boolean),
      });
    }
    return out;
  }, [bags, packTypeMap]);

  const columns = useMemo(() => [
    {
      key: 'Status', label: 'Status', interactive: true, cardRole: 'status',
      sortValue: (g) => pidStatusRank(g.rows?.[0]?.Status),
      render: (g) => (
        <StatusPicker
          status={g.rows?.[0]?.Status}
          confirm
          size="sm"
          statuses={PID_STATUSES}
          colorFor={pidStatusColor}
          normalize={normalizePidStatus}
          ariaLabel={`Status for label ${g.labelId}`}
          onChange={(next) => handleStatus(g.labelId, next, g.baseInfo?.LABEL_ID)}
        />
      ),
    },
    { key: 'Type', label: 'Type', sortValue: (g) => g.baseInfo?.Type || '', render: (g) => g.baseInfo?.Type },
    { key: 'Retailer', label: 'Retailer', sortValue: (g) => g.baseInfo?.Retailer || '', render: (g) => g.baseInfo?.Retailer },
    { key: 'LABEL_ID', label: 'Label ID', emphasis: true, cardRole: 'title', sortValue: (g) => g.labelId || '', render: (g) => g.baseInfo?.LABEL_ID },
    {
      key: 'Commodity', label: 'Commodity',
      sortValue: (g) => g.commodityNames[0] || '',
      render: (g) => <CommodityChips names={g.commodityNames} />,
    },
  ], [handleStatus]);

  const searchText = useCallback((g) => [
    g.rows?.[0]?.Status, g.baseInfo?.Type, g.baseInfo?.Retailer, g.baseInfo?.LABEL_ID, ...g.commodityNames,
  ].filter(Boolean).join(' '), []);

  return (
    <PageWrapper>
      <ContentWrapper>
        <PidTableShell
          title="PID Labels"
          columns={columns}
          rows={groups}
          rowKey={(g) => g.labelId}
          searchText={searchText}
          defaultSort={{ key: 'LABEL_ID', dir: 'asc' }}
          onAdd={() => setShowAddPanel(true)}
          onRowClick={(g) => {
            const commMap = {};
            for (const c of g.commodities) commMap[c.name] = [...c.styles];
            setEditGroup({ labelId: g.labelId, baseInfo: g.baseInfo, commodities: commMap, rows: g.rows });
          }}
          rowAriaLabel={(g) => `Edit label ${g.baseInfo?.LABEL_ID || g.labelId}`}
          loading={loading}
          loadingText="Loading PID Labels..."
          emptyText="No PID Labels found."
        />

        {showAddPanel && (
          <PidBagModal
            mode="create"
            commodities={commodities}
            packTypes={packTypeOptions}
            onClose={() => { setShowAddPanel(false); fetchAll(); }}
          />
        )}

        {editGroup && (
          <PidBagModal
            mode="edit"
            groupData={editGroup}
            commodities={commodities}
            packTypes={packTypeOptions}
            onClose={() => { setEditGroup(null); fetchAll(); }}
          />
        )}

        {dep && (
          <DependentsBlockDialog
            open
            scope="pid"
            subjectName={dep.name}
            dependents={dep.dependents}
            onClose={() => setDep(null)}
          />
        )}
      </ContentWrapper>
    </PageWrapper>
  );
};

export default PidBagsPage;
